import authService from './authService';
import { ApiResponse } from '../types/common';
import type { CancelledOrderWriteOffResult, CardDivergencePanel } from '../types/dashboard';
import type {
  RevenueReport,
  RevenueCityRow,
  ExpensesReport,
  ExpenseTransaction,
  ExpensesHistory,
  ExpensesAnnualReport,
  DreReport,
  DreBreakdown,
  DreInvoicesBreakdown,
  DreRevenueStream,
  ReportBasis,
  CashflowRealizedReport,
  CashflowProjectedReport,
  CashflowTransaction,
  SupplierMonthReport,
  SupplierForecast,
  PaymentMethodsReport,
} from '../types/financialReports';
import type {
  BankReconciliationReport,
  CardReceivableSchedule,
  CardReceivableSearchModel,
  CardSettlementDetail,
  SettlementApplyRequest,
  SettlementApplyResult,
  SettlementCoverage,
  SettlementDayDecisionRequest,
  SettlementDayDetail,
  SettlementImportDiff,
  SettlementReport,
} from '../types/cardReceivable';

/**
 * Financial Reports Service — Relatórios > Financeiro
 *
 * Receitas, Despesas, DRE e Fluxo de Caixa (Realizado/Projetado).
 * Todos os endpoints usam o envelope {success, data}.
 * `companyId` omitido = CONSOLIDADO (todas as empresas).
 */
class FinancialReportsService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  /** Extrai a mensagem de erro do corpo da resposta (ou usa o fallback). */
  private async throwApiError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
      const body = await response.json();
      message = body?.message || body?.error || fallback;
    } catch {
      /* corpo não-JSON — mantém fallback */
    }
    throw new Error(message);
  }

  /**
   * Download de arquivo (CSV) via fetch → blob → link temporário,
   * seguindo o padrão de financialService.downloadFile.
   */
  private async downloadFile(url: string, fallbackFilename: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}${url}`, {
        method: 'GET',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      await this.throwApiError(response, `Erro ao exportar arquivo (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? fallbackFilename;

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
  }

  /**
   * Monta querystring ano/mês/empresa (companyId omitido = consolidado).
   *
   * O `basis` só é enviado quando é 'projetado': omiti-lo no realizado mantém
   * as URLs byte-a-byte iguais às de antes desta funcionalidade, que é como a
   * não-regressão fica verificável por diff.
   */
  private periodParams(
    year: number,
    month?: number | null,
    companyId?: number | null,
    basis?: ReportBasis,
  ): URLSearchParams {
    const params = new URLSearchParams({ year: String(year) });
    if (month != null) params.set('month', String(month));
    if (companyId != null) params.set('companyId', String(companyId));
    if (basis === 'projetado') params.set('basis', basis);
    return params;
  }

  // ─── Fornecedores ──────────────────────────────────────────────

  /** GET /reports/financial/suppliers?year=&month=[&companyId=] — pagamentos por fornecedor no mês */
  async getSuppliersReport(year: number, month: number, companyId?: number | null): Promise<SupplierMonthReport> {
    const params = this.periodParams(year, month, companyId);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/suppliers?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar relatório de fornecedores');
    const data: ApiResponse<SupplierMonthReport> = await response.json();
    return data.data!;
  }

  /** GET /reports/financial/suppliers/forecast?months=[&companyId=] — matriz fornecedor × mês */
  async getSuppliersForecast(months: number, companyId?: number | null): Promise<SupplierForecast> {
    const params = new URLSearchParams({ months: String(months) });
    if (companyId != null) params.set('companyId', String(companyId));
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/suppliers/forecast?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar previsão de fornecedores');
    const data: ApiResponse<SupplierForecast> = await response.json();
    return data.data!;
  }

  // ─── Receitas ──────────────────────────────────────────────────

  /** GET /reports/financial/revenue?year=[&month=][&companyId=] — month null/omitido = ANUAL */
  async getRevenueReport(year: number, month: number | null, companyId?: number | null): Promise<RevenueReport> {
    const params = this.periodParams(year, month, companyId);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/revenue?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar relatório de receitas');
    const data: ApiResponse<RevenueReport> = await response.json();
    return data.data!;
  }

  /**
   * GET /reports/financial/revenue/cities?year=[&month=][&state=] — vendas por
   * município. Sem `state` devolve o ranking nacional (mesmo top que já vem no
   * relatório); com `state`, TODOS os municípios daquela UF — é o drill do mapa.
   */
  async getRevenueCities(year: number, month: number | null, state?: string): Promise<RevenueCityRow[]> {
    const params = this.periodParams(year, month);
    if (state) params.set('state', state);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/revenue/cities?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar vendas por cidade');
    const data: ApiResponse<RevenueCityRow[]> = await response.json();
    return data.data ?? [];
  }

  /** GET /reports/financial/revenue/export?year=&month=[&companyId=] — download CSV */
  async exportRevenueCsv(year: number, month: number, companyId?: number | null): Promise<void> {
    const params = this.periodParams(year, month, companyId);
    await this.downloadFile(
      `/reports/financial/revenue/export?${params}`,
      `receitas-${year}-${String(month).padStart(2, '0')}.csv`,
    );
  }

  // ─── Meios de pagamento das vendas ──────────────────────────────

  /** Querystring do relatório de meios de pagamento (companyId omitido = consolidado). */
  private paymentMethodsParams(dateFrom: string, dateTo: string, companyId?: number | null): URLSearchParams {
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (companyId != null) params.set('companyId', String(companyId));
    return params;
  }

  /** GET /reports/financial/payment-methods?date_from=AAAA-MM&date_to=AAAA-MM[&companyId=] */
  async getPaymentMethodsReport(dateFrom: string, dateTo: string, companyId?: number | null): Promise<PaymentMethodsReport> {
    const params = this.paymentMethodsParams(dateFrom, dateTo, companyId);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/payment-methods?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar relatório de meios de pagamento');
    const data: ApiResponse<PaymentMethodsReport> = await response.json();
    return data.data!;
  }

  /** PDF gerado no back (gráfico + tabelas mês a mês + bandeiras). */
  async downloadPaymentMethodsPdf(dateFrom: string, dateTo: string, companyId?: number | null): Promise<void> {
    const params = this.paymentMethodsParams(dateFrom, dateTo, companyId);
    await this.downloadFile(`/reports/financial/payment-methods/pdf?${params}`, `meios-de-pagamento-${dateFrom}-a-${dateTo}.pdf`);
  }

  /** Excel gerado no back (abas Consolidado, uma por empresa e Bandeiras). */
  async downloadPaymentMethodsExcel(dateFrom: string, dateTo: string, companyId?: number | null): Promise<void> {
    const params = this.paymentMethodsParams(dateFrom, dateTo, companyId);
    await this.downloadFile(`/reports/financial/payment-methods/excel?${params}`, `meios-de-pagamento-${dateFrom}-a-${dateTo}.xlsx`);
  }

  // ─── Despesas ──────────────────────────────────────────────────

  /** GET /reports/financial/expenses?year=&month=[&companyId=][&basis=projetado] */
  async getExpensesReport(
    year: number,
    month: number,
    companyId?: number | null,
    basis?: ReportBasis,
  ): Promise<ExpensesReport> {
    const params = this.periodParams(year, month, companyId, basis);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/expenses?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar relatório de despesas');
    const data: ApiResponse<ExpensesReport> = await response.json();
    return data.data!;
  }

  /** GET /reports/financial/expenses/transactions?year=[&month=]&erpId=[&companyId=] — drill-down (month omitido = ano inteiro) */
  async getExpenseTransactions(
    year: number,
    month: number | null | undefined,
    erpId: number,
    companyId?: number | null,
    basis?: ReportBasis,
  ): Promise<ExpenseTransaction[]> {
    const params = this.periodParams(year, month, companyId, basis);
    params.set('erpId', String(erpId));
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/expenses/transactions?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar lançamentos da categoria');
    const data: ApiResponse<ExpenseTransaction[]> = await response.json();
    return data.data ?? [];
  }

  /** GET /reports/financial/expenses/history?months=12[&companyId=][&dreGroupIds=1,2][&erpIds=...] */
  async getExpensesHistory(
    months: number,
    companyId?: number | null,
    dreGroupIds?: number[],
    erpIds?: number[],
  ): Promise<ExpensesHistory> {
    const params = new URLSearchParams({ months: String(months) });
    if (companyId != null) params.set('companyId', String(companyId));
    if (dreGroupIds && dreGroupIds.length > 0) params.set('dreGroupIds', dreGroupIds.join(','));
    if (erpIds && erpIds.length > 0) params.set('erpIds', erpIds.join(','));
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/expenses/history?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar histórico de despesas');
    const data: ApiResponse<ExpensesHistory> = await response.json();
    return data.data ?? { series: [], labels: {} };
  }

  /** GET /reports/financial/expenses/annual?mode=<mode>&year=<year>[&companyId=] — visão anual */
  async getExpensesAnnual(
    mode: 'year' | 'last12',
    year: number,
    companyId?: number | null,
    basis?: ReportBasis,
  ): Promise<ExpensesAnnualReport> {
    const params = new URLSearchParams({ mode, year: String(year) });
    if (companyId != null) params.set('companyId', String(companyId));
    // Em mode="last12" a grade já termina na competência corrente, então o
    // basis é aceito pela API e não tem efeito.
    if (basis === 'projetado') params.set('basis', basis);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/expenses/annual?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar visão anual de despesas');
    const data: ApiResponse<ExpensesAnnualReport> = await response.json();
    return data.data!;
  }

  // ─── DRE ───────────────────────────────────────────────────────

  /** GET /reports/financial/dre?year=[&month=][&companyId=] — month omitido = ANUAL */
  async getDreReport(
    year: number,
    month?: number | null,
    companyId?: number | null,
    basis?: ReportBasis,
  ): Promise<DreReport> {
    const params = this.periodParams(year, month, companyId, basis);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/dre?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar DRE');
    const data: ApiResponse<DreReport> = await response.json();
    return data.data!;
  }

  /** GET /reports/financial/dre/breakdown?year=&dreGroupId=[&month=][&companyId=] — memória de cálculo (month omitido = anual) */
  async getDreBreakdown(
    year: number,
    dreGroupId: number,
    month?: number | null,
    companyId?: number | null,
    basis?: ReportBasis,
  ): Promise<DreBreakdown> {
    const params = this.periodParams(year, month, companyId, basis);
    params.set('dreGroupId', String(dreGroupId));
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/dre/breakdown?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar memória de cálculo');
    const data: ApiResponse<DreBreakdown> = await response.json();
    return data.data!;
  }

  /** GET /reports/financial/dre/invoices?year=&stream=[&month=][&companyId=] — notas fiscais das receitas (month omitido = anual) */
  async getDreInvoices(
    year: number,
    stream: DreRevenueStream,
    month?: number | null,
    companyId?: number | null,
  ): Promise<DreInvoicesBreakdown> {
    const params = this.periodParams(year, month, companyId);
    params.set('stream', stream);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/dre/invoices?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar notas fiscais do período');
    const data: ApiResponse<DreInvoicesBreakdown> = await response.json();
    return data.data!;
  }

  // ─── Fluxo de Caixa ────────────────────────────────────────────

  /** GET /reports/financial/cashflow/realized?year=&month=[&accountId=] */
  async getCashflowRealized(year: number, month: number, accountId?: number | null): Promise<CashflowRealizedReport> {
    const params = new URLSearchParams({ year: String(year), month: String(month) });
    if (accountId != null) params.set('accountId', String(accountId));
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/cashflow/realized?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar fluxo de caixa realizado');
    const data: ApiResponse<CashflowRealizedReport> = await response.json();
    return data.data!;
  }

  /** GET /reports/financial/cashflow/transactions?year=&month=[&accountId=] — lançamentos do mês (expansão por dia) */
  async getCashflowTransactions(
    year: number,
    month: number,
    accountId?: number | null,
  ): Promise<CashflowTransaction[]> {
    const params = new URLSearchParams({ year: String(year), month: String(month) });
    if (accountId != null) params.set('accountId', String(accountId));
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/cashflow/transactions?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar lançamentos do fluxo de caixa');
    // O backend envelopa a lista em { transactions: [...] } (domain.FinancialCashflowTransactions),
    // então o array fica em data.data.transactions — não diretamente em data.data.
    const data: ApiResponse<{ transactions: CashflowTransaction[] }> = await response.json();
    return data.data?.transactions ?? [];
  }

  /** GET /reports/financial/cashflow/projected?months=6[&companyId=] */
  async getCashflowProjected(months: number, companyId?: number | null): Promise<CashflowProjectedReport> {
    const params = new URLSearchParams({ months: String(months) });
    if (companyId != null) params.set('companyId', String(companyId));
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/cashflow/projected?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar fluxo de caixa projetado');
    const data: ApiResponse<CashflowProjectedReport> = await response.json();
    return data.data ?? { months: [] };
  }

  /**
   * Agenda de recebíveis de cartão — o detalhe por trás da previsão que entra
   * no fluxo de caixa projetado: dia → bandeira → parcela do pedido.
   * Sem datas, a API devolve os próximos 60 dias.
   */
  async getCardReceivables(search: CardReceivableSearchModel = {}): Promise<CardReceivableSchedule> {
    const params = new URLSearchParams();
    if (search.dateFrom) params.set('date_from', search.dateFrom);
    if (search.dateTo) params.set('date_to', search.dateTo);
    if (search.companyId != null) params.set('company_id', String(search.companyId));
    if (search.onlyPending) params.set('only_pending', 'true');

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/card-receivables?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar os recebíveis de cartão');
    const data: ApiResponse<CardReceivableSchedule> = await response.json();
    return (
      data.data ?? {
        days: [],
        gross_amount: 0,
        fee_amount: 0,
        net_amount: 0,
        count: 0,
        without_fee_count: 0,
      }
    );
  }

  /**
   * Confere a planilha de recebíveis da Cielo (XLSX ou CSV) contra a agenda
   * prevista. NÃO grava nada: devolve as diferenças para o usuário decidir.
   */
  async previewCieloSettlement(file: File, companyId: number): Promise<SettlementReport> {
    const form = new FormData();
    form.append('file', file);
    // A empresa é a dona da conta corrente escolhida na tela: o backend valida o
    // CNPJ dela contra o declarado na planilha.
    form.append('company_id', String(companyId));

    // multipart não pode passar pelo apiClient (ele força Content-Type json);
    // mesmo padrão de financialService.postMultipart, com fallback de token.
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/reports/financial/card-receivables/settlement-preview`;
    const doFetch = async (token?: string | null) =>
      fetch(url, {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      response = await doFetch(await authService.getAuthToken(false));
    }
    if (!response.ok) await this.throwApiError(response, 'Falha ao conferir a planilha da Cielo');
    const data: ApiResponse<SettlementReport> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao conferir a planilha');
    return data.data;
  }

  /**
   * Importa o arquivo da Cielo no LEDGER POR DATA: upsert das linhas abertas do
   * período (nunca empilha lote) e resposta em forma de diff por data.
   */
  async importCieloLedger(file: File, companyId: number): Promise<SettlementImportDiff> {
    const form = new FormData();
    form.append('file', file);
    form.append('company_id', String(companyId));

    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/reports/financial/card-receivables/settlement-import`;
    const doFetch = async (token?: string | null) =>
      fetch(url, {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      response = await doFetch(await authService.getAuthToken(false));
    }
    if (!response.ok) await this.throwApiError(response, 'Falha ao importar a planilha da Cielo');
    const data: ApiResponse<SettlementImportDiff> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao importar a planilha');
    return data.data;
  }

  /** Visão por data (calendário/lista) da conciliação Cielo. */
  async getCieloCoverage(companyId: number, dateFrom: string, dateTo: string): Promise<SettlementCoverage> {
    const params = new URLSearchParams({
      company_id: String(companyId),
      date_from: dateFrom,
      date_to: dateTo,
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/card-receivables/settlement-coverage?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar a cobertura da conciliação');
    const data: ApiResponse<SettlementCoverage> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao carregar a cobertura');
    return data.data;
  }

  /** Grupos de uma data (abertos e aplicados). */
  async getCieloDay(companyId: number, date: string): Promise<SettlementDayDetail> {
    const params = new URLSearchParams({ company_id: String(companyId), date });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/card-receivables/settlement-day?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar o dia');
    const data: ApiResponse<SettlementDayDetail> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao carregar o dia');
    return data.data;
  }

  /**
   * Divergências que sobram depois da realocação por TID e da baixa
   * automática: "pagou a menos" e parcela ausente de todos os arquivos.
   * Lista completa — o dashboard mostra o mesmo painel cortado em top 5.
   */
  async getCieloDivergences(): Promise<CardDivergencePanel> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/reports/financial/card-receivables/divergences');
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar as divergências');
    const data: ApiResponse<CardDivergencePanel> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao carregar as divergências');
    return data.data;
  }

  /**
   * "Não vamos receber": retira da previsão as parcelas de uma venda cujo
   * pedido foi cancelado no ERP sem estorno pelo hub (chargeback ou
   * cancelamento feito no portal da Cielo).
   */
  async writeOffCancelledOrder(creditCardPaymentId: number): Promise<CancelledOrderWriteOffResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      '/reports/financial/card-receivables/cancelled-orders/write-off',
      { credit_card_payment_id: creditCardPaymentId },
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao retirar as parcelas da previsão');
    const data: ApiResponse<CancelledOrderWriteOffResult> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao retirar as parcelas da previsão');
    return data.data;
  }

  /** Aplica decisões sobre linhas abertas do ledger (baixar / não recebido). */
  async decideCieloDay(request: SettlementDayDecisionRequest): Promise<SettlementApplyResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      '/reports/financial/card-receivables/settlement-day/decide',
      request,
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao aplicar as decisões');
    const data: ApiResponse<SettlementApplyResult> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao aplicar as decisões');
    return data.data;
  }

  /**
   * Aplica as decisões do usuário sobre um lote conferido. É o ÚNICO ponto em
   * que a conferência baixa recebíveis e lança na conta corrente.
   */
  async applyCieloSettlement(request: SettlementApplyRequest): Promise<SettlementApplyResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      '/reports/financial/card-receivables/settlement-apply',
      request,
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao aplicar a conferência');
    const data: ApiResponse<SettlementApplyResult> = await response.json();
    if (!data.data) throw new Error('Resposta vazia ao aplicar a conferência');
    return data.data;
  }

  /**
   * Conciliação final: a agenda de cartão contra o que entrou na conta, dia a
   * dia. A fonte do extrato é o que já está lançado na conta corrente.
   */
  async getBankReconciliation(
    companyId: number,
    accountId: number,
    dateFrom: string,
    dateTo: string,
  ): Promise<BankReconciliationReport> {
    const params = new URLSearchParams({
      company_id: String(companyId),
      account_id: String(accountId),
      date_from: dateFrom,
      date_to: dateTo,
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/financial/card-receivables/bank-reconciliation?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao conciliar o extrato');
    const data: ApiResponse<BankReconciliationReport> = await response.json();
    if (!data.data) throw new Error('Resposta vazia na conciliação');
    return data.data;
  }

  /**
   * Detalhamento do crédito da adquirente: de qual depósito o lançamento veio e
   * quais parcelas/pedidos o compõem. É o que a linha do extrato abre.
   */
  async getCardSettlementDetail(transactionId: number): Promise<CardSettlementDetail> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/transactions/${transactionId}/card-settlement`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar o detalhamento do depósito');
    const data: ApiResponse<CardSettlementDetail> = await response.json();
    if (!data.data) throw new Error('Resposta vazia no detalhamento do depósito');
    return data.data;
  }
}

export const financialReportsService = new FinancialReportsService();
export default financialReportsService;
